import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  CircularProgress,
  Avatar,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import { SnackbarMessage, useSnackbar } from "../../hooks";
import { createSitesClientApi } from "../../api-client-nswag/taxportal-client-runtime";
import {
  ProjectsCustomizationDataRequestDto,
  ProjectsCustomizationDataDto,
  ProjectPerson,
} from "../../api-client-nswag/create-sites-client";
import "./ProjectDetailsDialog.scss";

interface EngagementManager {
  id: number | string;
  initials: string;
  name: string;
  email: string;
  employeenumber?: string;
  projectidmdm?: number | null;
  visibility: boolean;
}

interface ProjectDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  projectDefaultName: string;
  projectCustomName?: string;
  projectIdMdm: number | undefined;
  siteId: number;
  engagementManagers: ProjectPerson[] | undefined ;
  entityId?: number;
  entityName?: string;
  onUpdate?: (args?: {
    projectIdMdm: number;
    projectCustomName?: string;
    engagementManagers?: ProjectPerson[];
  }) => void;
}

const ProjectDetailsDialog: React.FC<ProjectDetailsDialogProps> = ({
  open,
  onClose,
  projectDefaultName,
  projectCustomName,
  projectIdMdm,
  siteId,
  engagementManagers,
  entityId,
  entityName,
  onUpdate,
}) => {
  const [activeTab, setActiveTab] = useState<"project" | "team">("project");
  const [managers, setManagers] = useState<EngagementManager[]>([]);
  const originalManagersRef = useRef<EngagementManager[]>([]);
  const [renameProject, setRenameProject] = useState(projectCustomName || "");
  const RENAME_PROJECT_MAX = 255;
  const renameProjectLimitError = renameProject.length > RENAME_PROJECT_MAX;
  const [saving, setSaving] = useState(false);
  const [confirmExitSettingsOpen, setConfirmExitSettingsOpen] = useState(false);
  const { showSnackbar } = useSnackbar();
  const hasProjectNameChanged =
    renameProject.trim() !== (projectCustomName || "").trim();
  const isTeamDirty = useMemo(
    () => managers.some(m => {
      const orig = originalManagersRef.current.find(o => o.email === m.email);
      return orig ? orig.visibility !== m.visibility : false;
    }),
    [managers]
  );
  const isDirty = isTeamDirty || hasProjectNameChanged;

  useEffect(() => {
    if (open) {
      setRenameProject(projectCustomName || "");
    }
  }, [open, projectCustomName]);

  useEffect(() => {
    if (open && engagementManagers && engagementManagers.length > 0) {
      const initialManagers: EngagementManager[] = engagementManagers.map(
        (manager, idx) => {
          // Handle both object and string formats
          let managerData = {
            id: 0,
            name: '',
            email: '',
            visibility: true
          };

          if (typeof manager === 'object' && manager !== null) {
            managerData.id = manager.id || idx;
            managerData.name = manager.name || '';
            managerData.email = manager.email || ''; 
            managerData.visibility = manager.visibility ?? true;
          } else if (typeof manager === 'string') {
            managerData.name = manager;
            managerData.id = idx;
          }

          // Extract initials from name
          const parts = managerData.name.trim().split(/\s+/);
          const initials =
            parts.length >= 2
              ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
              : managerData.name.substring(0, 2).toUpperCase();

          return {
            id: managerData.id || String(idx),
            initials,
            name: managerData.name,
            email: managerData.email,
            visibility: managerData.visibility ?? true,
          };
        }
      );
      setManagers(initialManagers);
      originalManagersRef.current = initialManagers.map(m => ({ ...m }));
    }
  }, [open, engagementManagers]);

  const handleVisibilityToggle = (email: string) => {
    setManagers((prev) =>
      prev.map((m) => (m.email === email ? { ...m, visibility: !m.visibility } : m))
    );
  };

  const handleSave = async () => {
    if (!projectIdMdm) {    
      showSnackbar({
        message: SnackbarMessage.FAILED_MESSAGE,
        severity: "error",
      });
      return;
    }

    try {
      setSaving(true);

      // Extract hidden engagement managers
      const hiddenManagers = managers
        .filter((m) => !m.visibility)
        .map((m) => m.email);

      const projectData: ProjectsCustomizationDataDto = {
        projectIdMDM: projectIdMdm,
        projectCustomName: hasProjectNameChanged ? renameProject.trim() : undefined,
        isVisible: undefined,
        engamentManagersToHide: isTeamDirty
          ? hiddenManagers
          : undefined,
        notes: undefined,
      };

      const requestData: ProjectsCustomizationDataRequestDto = {
        hqSiteId: siteId,
        projectsCustomizationDatas: [projectData],
      };

      await createSitesClientApi.tim_ProjectsCustomizationDataUpdate(
        requestData
      );

      showSnackbar({
        message: SnackbarMessage.PROJECT_DETAILS_UPDATED,
        severity: "success",
      });

      if (onUpdate) {
        onUpdate({
          projectIdMdm,
          projectCustomName: hasProjectNameChanged ? renameProject.trim() : projectCustomName,
          engagementManagers: managers.map((m) => ({
            id: m.id as any,
            name: m.name,
            email: m.email,
            visibility: m.visibility,
          })),
        });
      }

      originalManagersRef.current = managers.map(m => ({ ...m }));
      onClose();
    } catch (error) {      
      showSnackbar({
        message: SnackbarMessage.FAILED_MESSAGE,
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (isDirty) {
      setConfirmExitSettingsOpen(true);
    } else {
      onClose();
    }
  };

  const handleConfirmExitSettings = () => {
    setConfirmExitSettingsOpen(false);
    onClose();
  };

  const handleSaveAllSettings = async () => {
    setConfirmExitSettingsOpen(false);
    await handleSave();
  };

  const getAvatarColor = (): string => "#00a3e0";

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCancel}
        maxWidth="md"
        fullWidth
        className="project-details-dialog"
        PaperProps={{
          sx: {
            maxHeight: '85vh',
            maxWidth: '1000px',
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            margin: 'auto'
          }
        }}
      >
        <DialogTitle className="dialog-title-container">
          <div className="title-content">
            <div className="title-text">Project Details</div>
          </div>
          <IconButton
            aria-label="close"
            onClick={handleCancel}
            className="close-button"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers className="dialog-content">
          <Tabs
            value={activeTab}
            onChange={(_, newValue) => setActiveTab(newValue)}
            className="project-details-tabs"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Project Management" value="project" />
            <Tab label="Team Management" value="team" />
          </Tabs>

          {/* Project Management Tab */}
          {activeTab === "project" && (
            <div className="tab-content project-management-content">
              <div className="settings-description">
                Review the data for the selected project and enter a unique
                name in the editable field. This name will determine how the
                project is displayed to the client.
              </div>
              <div className="project-info-section">
                <div className="info-row">
                  <span className="label">Entity ID</span>
                  <span className="value">{entityId}</span>
                </div>
                <div className="info-row">
                  <span className="label">Entity</span>
                  <span className="value">{entityName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Project</span>
                  <span className="value project-link">{projectDefaultName}</span>
                </div>
              </div>

              <div className="rename-project-section">                
                 <div className="info-row">
                  <span className="rename-project-label">  Rename Project</span>
                </div>
                <TextField
                  size="small"
                  value={renameProject}
                  onChange={(e) => setRenameProject(e.target.value || "")}
                  placeholder="Enter new project name"
                  disabled={saving}
                  className="rename-project-input"
                />
                {renameProjectLimitError && (
                  <div className="rename-project-limit-error">
                    The entered value cannot exceed 255 characters.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Team Management Tab */}
          {activeTab === "team" && (
            <div className="tab-content team-management-content">
              <div className="settings-description">
                Select which Engagement Managers are visible to clients for this
                project. Use the toggle to control visibility for each manager.
              </div>
              <div className="project-info-section">
                <div className="info-row">
                  <span className="label">Entity ID</span>
                  <span className="value">{entityId}</span>
                </div>
                <div className="info-row">
                  <span className="label">Entity</span>
                  <span className="value">{entityName}</span>
                </div>
                <div className="info-row">
                  <span className="label">Project</span>
                  <span className="value project-link">{projectDefaultName}</span>
                </div>
              </div>

              {managers.length === 0 ? (
                <div className="no-data-container">
                  <Typography color="textSecondary">
                    No engagement managers assigned to this project.
                  </Typography>
                </div>
              ) : (
                <TableContainer component={Paper} className="managers-table">
                  <Table>
                    <TableHead>
                      <TableRow className="table-header">
                        <TableCell className="manager-cell">
                          Engagement Managers
                        </TableCell>
                        <TableCell align="right" className="visibility-cell">
                         Visibility
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {managers.map((manager) => (
                        <TableRow key={manager.id} className="manager-row">
                          <TableCell className="manager-info">
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  backgroundColor: getAvatarColor(),
                                  width: 38,
                                  height: 38,
                                  fontSize: "0.9rem"
                                }}
                              >
                                {manager.initials}
                              </Avatar>
                              <Box>
                                <Typography className="manager-name">
                                  {manager.name}
                                </Typography>
                                <Typography className="manager-email">
                                  {manager.email}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell
                            align="right"
                            className="visibility-cell"
                          >
                            <Button
                              size="large"
                              color="inherit"
                              startIcon={
                                manager.visibility ? (
                                  <ToggleOnIcon
                                    sx={{
                                      fontSize: "36px !important",
                                      color: "#00a3e0",
                                    }}
                                  />
                                ) : (
                                  <ToggleOffIcon
                                    sx={{
                                      fontSize: "36px !important",
                                      color: "#666666",
                                    }}
                                  />
                                )
                              }
                              onClick={() => handleVisibilityToggle(String(manager.email))}
                              disabled={saving}
                              sx={{ minWidth: "auto", padding: 0 }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className="dialog-actions">
          <Button
            onClick={handleCancel}
            disabled={saving}
            variant="outlined"
            className="cancel-button"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !isDirty || renameProjectLimitError}
            variant="contained"
            className="save-button"
          >
            {saving ? <CircularProgress size={20} /> : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmExitSettingsOpen}
        onClose={() => setConfirmExitSettingsOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: '8px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle sx={{ 
          fontSize: '20px', 
          fontWeight: 600,
          padding: '24px 24px 16px 24px'
        }}>
          Are you sure you want to cancel?
        </DialogTitle>
        <DialogContent sx={{ padding: '0 24px 24px 24px' }}>         
          <Typography sx={{ color: 'text.secondary' }}>
            You have unsaved changes. Leaving now will discard them.
          </Typography>        
        </DialogContent>
        <DialogActions sx={{ 
          padding: '16px 24px 24px 24px',
          gap: '12px'
        }}>
          <Button
            variant="outlined"
            onClick={handleConfirmExitSettings}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderColor: '#00a3e0',
              color: '#00a3e0',
              '&:hover': {
                borderColor: '#0082b3',
                backgroundColor: 'rgba(0, 163, 224, 0.04)'
              }
            }}
          >
            Exit
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAllSettings}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              backgroundColor: '#00a3e0',
              '&:hover': {
                backgroundColor: '#0082b3'
              }
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectDetailsDialog;
